<template>
  <FixedLoader v-if="loading" />
  <template v-else-if="!loading && data">
    <div class="hero hero-active">
      <div class="hero-image">
        <BlockContent
          class="hero-caption hero-caption-sm"
          :blocks="data.title"
          :render-container-on-single-child="true"
        />
        <img
          :srcset="data.hero.mobile.srcset"
          :src="data.hero.mobile.src"
          class="w-1/2 object-contain block md:hidden"
        />
        <img
          :srcset="data.hero.desktop.srcset"
          :src="data.hero.desktop.src"
          class="w-1/2 h-full object-contain hidden md:block"
        />
      </div>

      <BlockContent
        class="hero-caption hero-caption-lg"
        :blocks="data.title"
        :render-container-on-single-child="true"
      />
    </div>

    <div class="hero-links">
      <transition name="fade" appear>
        <a
          v-if="data.content"
          href="#story"
          class="group"
          @click.prevent="scrollTo('#story')"
          ><span class="link-dot" /> Read</a
        >
      </transition>
      <transition name="fade" appear>
        <a
          v-if="data.works?.length"
          href="#works"
          class="group"
          @click.prevent="scrollTo('#works', 96)"
          ><span class="link-dot" />
          <span class="hidden xl:inline"> Available</span> Works</a
        >
      </transition>
    </div>

    <div v-if="data.content" id="story" class="pt-12 -mt-12 lg:pt-6 lg:-mt-6">
      <div class="max-w-4xl story-section">
        <div class="mx-8">
          <div v-if="data.author">{{ data.author }}</div>
          <div v-if="data.hero?.attribution">{{ data.hero.attribution }}</div>
        </div>
      </div>
      <StoryContent :content="data.content" />
    </div>

    <WorksGrid
      id="works"
      :works="data.works || []"
      @enter="onWorksEnter"
      @leave="onWorksLeave"
    />
  </template>
</template>

<script lang="ts">
import { defineComponent, onMounted } from 'vue';
import animateScrollTo from 'animated-scroll-to';
import { useRoute } from 'vue-router';
import StoryContent from '@/components/StoryContent.vue';
import WorksGrid from '@/components/WorksGrid.vue';
import getStory from '@/sanity/requests/story';
import { useStore } from '@/store';

export default defineComponent({
  name: 'Story',
  components: {
    StoryContent,
    WorksGrid,
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const slug = route.params.story as string;

    const scrollTo = (selector: string, offset = 0) => {
      const [el] = document.querySelectorAll(selector);
      if (el !== null && el instanceof HTMLElement) {
        animateScrollTo(el.offsetTop - offset, {
          cancelOnUserAction: false,
          maxDuration: 1000,
        });
      }
    };

    onMounted(() => {
      store.commit.setBackgroundWhite(false);
    });

    const onWorksEnter = () => {
      store.commit.setBackgroundWhite(true);
    };

    const onWorksLeave = () => {
      if (route.name === 'story') {
        store.commit.setBackgroundWhite(false);
      }
    };

    return { ...getStory(slug), scrollTo, onWorksEnter, onWorksLeave };
  },
});
</script>

<style lang="postcss">
.story-section {
  @apply mx-auto my-24;
}
</style>
